import React, { useCallback, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../DashboardComponents/DashboardCard"
import ListingTabs from "./ListingTabs"
import SearchInput from "./SearchInput"
import FilterDropdown from "./FilterDropdown"
import ExportLogButton from "./ExportLogButton"
import Pagination from "./Pagination"
import CustomButton from "../OnboardingComponents/CustomButton"
import EmptyState from "../Common/EmptyState"
import DeleteConfirmation from "../Common/DeleteConfirmation"
import TruncatedText from "../Common/TruncatedText"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../../constants/icons"
import { get, patch } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import useAdminPermission from "../../hooks/useAdminPermission"
import useAdminCityFilterOptions from "../../hooks/useAdminCityFilterOptions"
import AddMemberModal from "../../pages/Admin/Members/AddMemberModal"
import AdminCityListingTitle from "../AdminLayout/AdminCityListingTitle"
import {
  DEFAULT_MEMBER_SORT_BY,
  DEFAULT_MEMBER_SORT_DIRECTION,
  mapMemberKpi,
  mapMemberRow,
  mapMemberStatusTabs,
  MEMBER_LIST_WITH,
  MEMBER_SORT_OPTIONS,
  MEMBER_STATUS_TABS,
} from "../../pages/Admin/Members/adminMembersData"

const PAGE_SIZE = 10
const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

const STATUS_TONE = {
  active: "active",
  inactive: "inactive",
}

/**
 * Shared Members listing — GET /api/v1/admin/members
 * Tabs: All / Active / Inactive (status query param).
 */
const MembersListing = ({
  documentTitle,
  cityId,
  showKpi = true,
  staticKpiCards,
  showExport = true,
  showCityFilter = true,
  memberBasePath,
  profilePathForRow,
  inviteButtonLabel = "Add Member",
  statusTabsBase = MEMBER_STATUS_TABS,
  className = "",
}) => {
  const navigate = useNavigate()
  const { can, isReady, isSuperAdmin } = useAdminPermission()
  const canInviteCreate = can("invites", "create")
  const canUpdateMember = can("members", "edit")
  const [searchParams, setSearchParams] = useSearchParams()
  const isCityScoped = Boolean(cityId)
  const { cityOptions } = useAdminCityFilterOptions({
    enabled: showCityFilter && !isCityScoped,
  })
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [cityFilter, setCityFilter] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_MEMBER_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_MEMBER_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kpiCards, setKpiCards] = useState(
    () => staticKpiCards || mapMemberKpi(),
  )
  const [isKpiLoading, setIsKpiLoading] = useState(showKpi && !staticKpiCards)
  const [isExporting, setIsExporting] = useState(false)
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false)
  const [statusTabs, setStatusTabs] = useState(() => statusTabsBase)
  const [statusTarget, setStatusTarget] = useState(null)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  useEffect(() => {
    document.title = documentTitle
  }, [documentTitle])

  useEffect(() => {
    if (searchParams.get("create") !== "1") return
    if (!isReady && !isSuperAdmin) return
    if (canInviteCreate) setIsAddMemberOpen(true)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("create")
    setSearchParams(nextParams, { replace: true })
  }, [canInviteCreate, isReady, isSuperAdmin, searchParams, setSearchParams])

  useEffect(() => {
    setStatusTabs(statusTabsBase)
  }, [statusTabsBase])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, cityFilter, sortBy, sortDirection, cityId])

  useEffect(() => {
    if (!cityOptions.length || !cityFilter) return
    const allowed = new Set(cityOptions.map(option => String(option.value)))
    if (!allowed.has(String(cityFilter))) setCityFilter("")
  }, [cityFilter, cityOptions])

  const applyCityParams = useCallback(
    params => {
      const selectedCityId = cityId || cityFilter
      if (selectedCityId) params.city_id = selectedCityId
      return params
    },
    [cityFilter, cityId],
  )

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        "with[]": MEMBER_LIST_WITH,
        sort_by: sortBy || DEFAULT_MEMBER_SORT_BY,
        sort_direction: sortDirection || DEFAULT_MEMBER_SORT_DIRECTION,
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = PAGE_SIZE
      }

      if (debouncedSearch) params.search = debouncedSearch
      if (activeTab === "active" || activeTab === "inactive") {
        params.status = activeTab
      }

      return applyCityParams(params)
    },
    [
      activeTab,
      applyCityParams,
      currentPage,
      debouncedSearch,
      sortBy,
      sortDirection,
    ],
  )

  const buildExportParams = useCallback(() => {
    const params = {
      sort_by: sortBy || DEFAULT_MEMBER_SORT_BY,
      sort_direction: sortDirection || DEFAULT_MEMBER_SORT_DIRECTION,
    }
    if (debouncedSearch) params.search = debouncedSearch
    if (activeTab === "active" || activeTab === "inactive") {
      params.status = activeTab
    }
    return applyCityParams(params)
  }, [activeTab, applyCityParams, debouncedSearch, sortBy, sortDirection])

  const fetchMembers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminMembers, {
        params: buildListParams(),
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapMemberRow)
        setRows(mapped)
        setTotalItems(result.total || mapped.length)
        setPageSize(result.per_page || result.page_size || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total || mapped.length) /
              (result.per_page || result.page_size || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load members")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load members")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  const applyMembersSummary = useCallback(
    result => {
      if (showKpi && !staticKpiCards) {
        setKpiCards(mapMemberKpi(result))
      }
      setStatusTabs(mapMemberStatusTabs(result, statusTabsBase))
    },
    [showKpi, staticKpiCards, statusTabsBase],
  )

  const refreshSummary = useCallback(async () => {
    try {
      const params = {}
      if (cityId) params.city_id = cityId
      const response = await get(endpoints.getAdminMembersSummary, {
        params,
        skipErrorToast: true,
      })
      if (!response?.data?.success) return
      applyMembersSummary(response.data.result || {})
    } catch {
      // list refresh already succeeded
    }
  }, [applyMembersSummary, cityId])

  useEffect(() => {
    if (isCityScoped && !cityId) {
      setIsKpiLoading(false)
      return undefined
    }
    if (showKpi && !staticKpiCards) setIsKpiLoading(true)

    let cancelled = false

    const fetchSummary = async () => {
      try {
        const params = {}
        if (cityId) params.city_id = cityId
        const response = await get(endpoints.getAdminMembersSummary, {
          params,
          skipErrorToast: true,
        })
        if (cancelled) return
        if (response?.data?.success) {
          applyMembersSummary(response.data.result || {})
        }
      } catch {
        // summary is non-blocking
      } finally {
        if (!cancelled) setIsKpiLoading(false)
      }
    }

    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [applyMembersSummary, cityId, isCityScoped, showKpi, staticKpiCards])

  useEffect(() => {
    fetchMembers()
  }, [fetchMembers])

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  const filterConfig = useMemo(() => {
    const filters = [
      {
        key: "sortBy",
        type: "sortBy",
        label: "Sort By",
        value: sortBy,
        defaultValue: DEFAULT_MEMBER_SORT_BY,
        onChange: next => {
          setSortBy(next || DEFAULT_MEMBER_SORT_BY)
        },
        options: MEMBER_SORT_OPTIONS,
      },
      {
        key: "sortDirection",
        type: "sortDirection",
        label: "Order",
        value: sortDirection,
        defaultValue: DEFAULT_MEMBER_SORT_DIRECTION,
        onChange: next => {
          setSortDirection(next || DEFAULT_MEMBER_SORT_DIRECTION)
        },
      },
    ]

    if (showCityFilter && !isCityScoped) {
      filters.push({
        key: "city",
        type: "select",
        label: "City",
        multiple: false,
        searchable: true,
        options: cityOptions,
        value: cityFilter,
        defaultValue: "",
        onChange: next => {
          const value = Array.isArray(next)
            ? String(next[0] || "")
            : String(next || "")
          setCityFilter(value)
        },
      })
    }

    return filters
  }, [
    cityFilter,
    cityOptions,
    isCityScoped,
    showCityFilter,
    sortBy,
    sortDirection,
  ])

  const resolveProfilePath = row => {
    if (typeof profilePathForRow === "function") {
      return profilePathForRow(row)
    }
    if (!memberBasePath || !row?.email || row.email === "--") return ""
    return `${memberBasePath}/${encodeURIComponent(row.email)}`
  }

  const handleViewDetail = row => {
    const path = resolveProfilePath(row)
    if (!path) {
      toast.error("Member profile is not available yet.", TOAST_OPTIONS)
      return
    }
    navigate(path)
  }

  const closeStatusModal = () => {
    if (isUpdatingStatus) return
    setStatusTarget(null)
  }

  const handleStatusConfirm = async () => {
    if (!statusTarget?.id || isUpdatingStatus) return
    const nextActive = !statusTarget.isActive
    setIsUpdatingStatus(true)
    try {
      const response = await patch(
        endpoints.updateAdminStaffStatus(statusTarget.id),
        { is_active: nextActive },
        { skipErrorToast: true },
      )
      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to update member status")
        return
      }
      toast.success(
        response?.data?.message ||
          (nextActive ? "Member activated." : "Member deactivated."),
        TOAST_OPTIONS,
      )
      setStatusTarget(null)
      await fetchMembers()
      await refreshSummary()
    } catch (err) {
      toastApiError(err, "Failed to update member status")
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const response = await get(endpoints.exportAdminMembers, {
        params: buildExportParams(),
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export members")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export members"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, TOAST_OPTIONS)
        return
      }

      const disposition = String(
        response.headers?.["content-disposition"] || "",
      )
      const filenameMatch = disposition.match(
        /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
      )
      const filename = decodeURIComponent(
        filenameMatch?.[1] || filenameMatch?.[2] || "members-export.csv",
      )

      const blob = new Blob([response.data], {
        type: contentType || "text/csv",
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      toast.success("Members exported.", TOAST_OPTIONS)
    } catch (err) {
      toastApiError(err, "Failed to export members")
    } finally {
      setIsExporting(false)
    }
  }

  const renderRowActions = row => {
    const hasProfileTarget =
      typeof profilePathForRow === "function" ||
      (memberBasePath && row.email && row.email !== "--")

    return (
      <>
        {hasProfileTarget ? (
          <CustomButton
            variant="transparent"
            size="sm"
            type="button"
            trailingIconSrc={ICONS.arrowRight12}
            trailingIconAlt=""
            onClick={() => handleViewDetail(row)}
          >
            View Detail
          </CustomButton>
        ) : null}
        {canUpdateMember ? (
          <CustomButton
            variant={row.isActive ? "danger" : "secondary"}
            size="sm"
            type="button"
            onClick={() => setStatusTarget(row)}
          >
            {row.isActive ? "Deactivate" : "Activate"}
          </CustomButton>
        ) : null}
      </>
    )
  }

  const listingTitle = isCityScoped ? (
    <AdminCityListingTitle label="Members" cityId={cityId} />
  ) : (
    <h1 className="admin-members__title">Members</h1>
  )

  return (
    <div
      className={`admin-page admin-members${className ? ` ${className}` : ""}`}
    >
      {showKpi ? (
        <div className="admin-members__kpi-grid">
          {kpiCards.map(card => (
            <DashboardCard
              key={card.key}
              title={card.title}
              tone={card.tone}
              className="admin-members__kpi-card"
              fullHeight
            >
              {isKpiLoading ? (
                <span className="admin-members__skeleton admin-members__skeleton--kpi" />
              ) : (
                <p className="admin-members__kpi-value">{card.value}</p>
              )}
            </DashboardCard>
          ))}
        </div>
      ) : null}

      <div className="admin-members__toolbar">
        {isCityScoped && !showKpi ? (
          listingTitle
        ) : (
          <div className="admin-members__toolbar-left">
            {listingTitle}
            {!isCityScoped || showKpi ? (
              <ListingTabs
                items={statusTabs}
                activeKey={activeTab}
                onChange={setActiveTab}
              />
            ) : null}
          </div>
        )}
        <div className="admin-members__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by Content..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-members__search"
          />
          <FilterDropdown label="Filter" filters={filterConfig} />
          {showExport ? (
            <ExportLogButton
              entityLabel="members"
              onClick={handleExport}
              loading={isExporting}
              disabled={isExporting}
            />
          ) : null}
          {canInviteCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={() => setIsAddMemberOpen(true)}
            >
              {inviteButtonLabel}
            </CustomButton>
          ) : null}
        </div>
      </div>

      {isCityScoped && !showKpi ? (
        <div className="admin-members__tabs-row">
          <ListingTabs
            items={statusTabs}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        </div>
      ) : null}

      <div className="admin-members__table-wrapper">
        <div
          className="admin-members__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-members__table-row admin-members__table-row--head"
            role="row"
          >
            <div role="columnheader">Member Name</div>
            <div role="columnheader">Email</div>
            <div role="columnheader">Cities</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Total XP</div>
            <div role="columnheader">Projects</div>
            <div role="columnheader" className="admin-members__actions-head">
              Actions
            </div>
          </div>

          {isLoading &&
            skeletonRows.map(key => (
              <div
                key={key}
                className="admin-members__table-row admin-members__table-row--skeleton"
                role="row"
              >
                <div className="admin-members__skeleton" />
                <div className="admin-members__skeleton" />
                <div className="admin-members__skeleton" />
                <div className="admin-members__skeleton" />
                <div className="admin-members__skeleton" />
                <div className="admin-members__skeleton" />
                <div className="admin-members__skeleton" />
              </div>
            ))}

          {!isLoading &&
            rows.map(row => (
              <div key={row.id} className="admin-members__table-row" role="row">
                <div className="admin-members__user" role="cell">
                  {row.image ? (
                    <img
                      className="admin-members__avatar admin-members__avatar--image"
                      src={row.image}
                      alt=""
                    />
                  ) : (
                    <Avatar
                      src={row.image}
                      name={row.name}
                      size="sm"
                      className="admin-members__avatar"
                    />
                  )}
                  <TruncatedText className="admin-members__name">
                    {row.name}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-members__cell"
                  role="cell"
                >
                  {row.email}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-members__cell admin-members__cell--cities"
                  role="cell"
                >
                  {row.city}
                </TruncatedText>
                <div className="admin-members__status-cell" role="cell">
                  <span
                    className={`admin-members__status admin-members__status--${
                      STATUS_TONE[row.status] || "inactive"
                    }`}
                  >
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-members__cell"
                  role="cell"
                >
                  {Number(row.totalXp || 0).toLocaleString("en-US")}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-members__cell"
                  role="cell"
                >
                  {Number(row.totalProjects || 0).toLocaleString("en-US")}
                </TruncatedText>
                <div className="admin-members__actions" role="cell">
                  {renderRowActions(row)}
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-members__empty">
              <EmptyState
                title="No members found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-members__empty">
              <EmptyState
                title="Couldn’t load members"
                description={error}
                actionText="Try again"
                onActionClick={fetchMembers}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Members"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-members__pagination"
      />

      <AddMemberModal
        isOpen={isAddMemberOpen}
        onClose={() => setIsAddMemberOpen(false)}
        onSuccess={() => {
          fetchMembers()
          refreshSummary()
        }}
        defaultCityId={cityId}
      />

      <DeleteConfirmation
        isOpen={Boolean(statusTarget)}
        onCancel={closeStatusModal}
        onConfirm={handleStatusConfirm}
        title={
          statusTarget?.isActive ? "Deactivate Member" : "Activate Member"
        }
        message={
          statusTarget
            ? statusTarget.isActive
              ? `Are you sure you want to deactivate “${statusTarget.name}”? They will no longer be able to access the platform.`
              : `Are you sure you want to activate “${statusTarget.name}”? They will regain access to the platform.`
            : ""
        }
        cancelText="Cancel"
        confirmText={statusTarget?.isActive ? "Deactivate" : "Activate"}
        confirmLoadingText={
          statusTarget?.isActive ? "Deactivating..." : "Activating..."
        }
        isDeleting={isUpdatingStatus}
      />
    </div>
  )
}

MembersListing.propTypes = {
  documentTitle: PropTypes.string.isRequired,
  cityId: PropTypes.string,
  showKpi: PropTypes.bool,
  staticKpiCards: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tone: PropTypes.string,
    }),
  ),
  showExport: PropTypes.bool,
  showCityFilter: PropTypes.bool,
  memberBasePath: PropTypes.string,
  profilePathForRow: PropTypes.func,
  inviteButtonLabel: PropTypes.string,
  statusTabsBase: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  className: PropTypes.string,
}

MembersListing.defaultProps = {
  cityId: undefined,
  showKpi: true,
  staticKpiCards: undefined,
  showExport: true,
  showCityFilter: true,
  memberBasePath: undefined,
  profilePathForRow: undefined,
  inviteButtonLabel: "Add Member",
  statusTabsBase: MEMBER_STATUS_TABS,
  className: "",
}

export default MembersListing
